import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Collapse, IconButton } from '@mui/material';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import dayjs from 'dayjs';
import { ReactNode, useEffect, useState } from 'react';
import { BaseService } from 'shared/services/BaseService';
import { IAugmentedAuditLog } from '../types';

export interface EntityHistoryProps {
  entityId: number | string;
  service: BaseService<any>;
}

function EntityHistory(props: EntityHistoryProps) {
  const { service, entityId } = props;
  const [history, setHistory] = useState<IAugmentedAuditLog[]>([]);
  const [expandedRows, setExpandedRows] = useState<Record<string | number, boolean>>({});

  const toggleRow = (id: string | number) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderChanges = (changes: string): ReactNode => {
    try {
      const parsedChanges = JSON.parse(changes);
      if (Object.keys(parsedChanges).length === 0)
        return (
          <div className="mt-3 p-3 bg-slate-50 border border-slate-100 rounded-lg">
            <p className="text-xs text-slate-400 m-0 font-medium">
              Nessuna modifica rilevata nei campi.
            </p>
          </div>
        );

      return (
        <div className="my-3 p-3 bg-slate-50 border border-slate-100 rounded-lg overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-2 bg-slate-100 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <div>Campo Modificato</div>
            <div>Valore Precedente</div>
            <div>Nuovo Valore</div>
          </div>

          <ul className="space-y-2 m-0 list-none pl-0">
            {Object.entries(parsedChanges).map(([key, value]) => {
              const val = value as [any, any];
              return (
                <li
                  key={key}
                  className="grid grid-cols-1 sm:grid-cols-3 gap-1 text-xs text-slate-600 border-b border-slate-100 pb-1.5 last:border-0 last:pb-0">
                  <span className="font-semibold text-slate-700 capitalize truncate">
                    {key.replace('_', ' ')}
                  </span>
                  <span className="line-through text-slate-400 truncate">
                    {String(val[0] ?? 'vuoto')}
                  </span>
                  <span className="text-emerald-600 font-medium truncate">
                    &rarr; {String(val[1] ?? 'vuoto')}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      );
    } catch (e) {
      return (
        <div className="mt-3 p-3 bg-slate-50 border border-slate-100 rounded-lg">
          <p className="text-xs text-slate-400 m-0">
            Nessun dettaglio disponibile per questa riga.
          </p>
        </div>
      );
    }
  };

  useEffect(() => {
    service.history(entityId).then(setHistory);
  }, [entityId, service]);

  const getEventStyle = (action: number | undefined, backendText?: string) => {
    switch (action) {
      case 0:
        return {
          bgColor: '#10b981', // emerald-500
          Icon: AddIcon,
          text: backendText || "ha creato l'elemento"
        };
      case 1:
        return {
          bgColor: '#3b82f6', // blue-500
          Icon: EditIcon,
          text: backendText || 'ha modificato i dettagli'
        };
      case 2:
        return {
          bgColor: '#f43f5e', // rose-500
          Icon: DeleteIcon,
          text: backendText || "ha eliminato l'elemento"
        };
      default:
        return {
          bgColor: '#64748b', // slate-500
          Icon: HistoryIcon,
          text: backendText || "ha effettuato un'operazione"
        };
    }
  };

  return (
    <IsTenantAdministrator>
      <EnhancedCard
        title="Registro delle attività"
        subtitle="Monitora la timeline degli eventi, gli utenti responsabili e il dettaglio dei campi modificati">
        <div className="flow-root">
          <ul role="list" className="-mb-8 pl-0 my-0">
            {history.map((event, eventIdx) => {
              const rowId = event.id || eventIdx;
              const isExpanded = !!expandedRows[rowId];

              // Conversione sicura in numero intero
              const actionNumber = event.action !== undefined ? Number(event.action) : undefined;
              const { bgColor, Icon, text } = getEventStyle(actionNumber, event.additional_data);

              return (
                <li key={rowId} className="list-none group">
                  <div className="relative pb-8">
                    {eventIdx !== history.length - 1 ? (
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-slate-200"
                        aria-hidden="true"
                      />
                    ) : null}
                    <div
                      className="relative flex items-start space-x-3 cursor-pointer"
                      onClick={() => toggleRow(rowId)}>
                      <div className="flex-shrink-0 pt-0.5">
                        <span
                          style={{ backgroundColor: bgColor }}
                          className="h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white shadow-sm text-white transition-transform group-hover:scale-105">
                          <Icon
                            className="h-4 w-4 text-white"
                            style={{ color: '#ffffff' }}
                            aria-hidden="true"
                          />
                        </span>
                      </div>
                      <div className="flex-1 min-w-0 pt-1.5">
                        <div className="flex justify-between items-center space-x-4">
                          <p className="text-sm text-slate-600 m-0">
                            <span className="text-slate-900 font-semibold">
                              {event.actor_id__username || 'Sistema'}
                            </span>{' '}
                            <span className="text-slate-500">{text}</span>
                          </p>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <time
                              className="text-xs text-slate-400 font-mono"
                              dateTime={event.timestamp}>
                              {dayjs(event.timestamp).format('DD/MM/YYYY HH:mm')}
                            </time>
                            <IconButton size="small" className="text-slate-400">
                              {isExpanded ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                              )}
                            </IconButton>
                          </div>
                        </div>

                        <Collapse
                          in={isExpanded}
                          timeout="auto"
                          unmountOnExit
                          onClick={(e) => e.stopPropagation()}>
                          {renderChanges(event.changes)}
                        </Collapse>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </EnhancedCard>
    </IsTenantAdministrator>
  );
}

export default EntityHistory;
